import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';

/**
 * Per-state market images for section 2 of the tender proposal.
 * Resolution at generation time lives in ../lib/stateMarketImage.ts.
 */

// `/api/scopingai`, no hyphen — that is how app.ts mounts the router (see retailerService,
// which calls /api/scopingai/retailers). A hyphenated path 404s with Express's HTML error page.
const API_BASE = (import.meta.env.VITE_API_URL ?? 'http://localhost:3001') + '/api/scopingai/state-images';

export type StateImageRow = {
  state_code: string;
  image_url: string;
  storage_path: string | null;
  updated_at: string;
};

/** Same shape every other service in this codebase returns (see billEmailService). */
export type ApiResult<T> = { success: boolean; data?: T; error?: string };

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  return {
    'x-api-key': 'scopingai',
    ...(session?.user?.id ? { 'x-user-id': session.user.id } : {}),
    ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
  };
}

/**
 * The server always answers JSON, but a request can still die upstream of it (gateway
 * timeout, proxy error page). Parsing as text first turns that into a readable message
 * instead of a raw "Unexpected token '<'" from JSON.parse.
 */
async function readJson(res: Response): Promise<{ json: any; error: string | null }> {
  const text = await res.text();
  try {
    return { json: text ? JSON.parse(text) : {}, error: null };
  } catch {
    const snippet = text.slice(0, 100).replace(/\s+/g, ' ').trim();
    return { json: null, error: `Request failed (${res.status})${snippet ? ` — ${snippet}` : ''}` };
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<ApiResult<T>> {
  try {
    const headers = await authHeaders();
    const res = await fetch(`${API_BASE}${path}`, { ...init, headers: { ...headers, ...(init?.headers ?? {}) } });
    const { json, error } = await readJson(res);
    if (error) return { success: false, error };
    if (!res.ok || !json.success) return { success: false, error: json?.error ?? `Request failed (${res.status})` };
    return { success: true, data: json.data as T };
  } catch (err: unknown) {
    return { success: false, error: (err as Error).message };
  }
}

/** What the list endpoint returns: the images, plus which states to offer and why. */
export type StateImagePayload = {
  images: StateImageRow[];
  /** States to offer — the ASX four, plus anything already configured. */
  states: string[];
};

export const stateImageService = {
  list: (): Promise<ApiResult<StateImagePayload>> => request(''),

  upload: async (state: string, file: File): Promise<ApiResult<StateImageRow>> => {
    const form = new FormData();
    form.append('image', file);
    // Content-Type is deliberately not set — the browser must add the multipart boundary.
    return request(`/${state}`, { method: 'PUT', body: form });
  },

  clear: (state: string): Promise<ApiResult<null>> => request(`/${state}`, { method: 'DELETE' }),
};
